import createDebugger, { type Debugger } from 'debug'
import type { LoggerObject, LoggerContext, LoggerLevel } from '@/shared/utils/logging/types'

const mapOfLoggers = new Map<string, Debugger>()

type LogHandler = (message: string, context?: LoggerContext) => void

const traceLogger = createDebugger('trace')
const debugLogger = createDebugger('debug')
const infoLogger = createDebugger('info')
const warningLogger = createDebugger('warning')
const errorLogger = createDebugger('error')
const criticalLogger = createDebugger('critical')

class Logger {
  #context: LoggerContext = { }

  #mergeContext(context?: LoggerContext) {
    return { ...this.#context, ...(context ?? { }) }
  }

  withContext(context: LoggerContext) {
    const logger = new Logger()
    logger.#context = context

    return logger
  }

  trace(message: string, context?: LoggerContext) { traceLogger(message, this.#mergeContext(context)) }
  debug(message: string, context?: LoggerContext) { debugLogger(message, this.#mergeContext(context)) }
  info(message: string, context?: LoggerContext) { infoLogger(message, this.#mergeContext(context)) }
  warning(message: string, context?: LoggerContext) { warningLogger(message, this.#mergeContext(context)) }
  error(message: string, context?: LoggerContext) { errorLogger(message, this.#mergeContext(context)) }
  critical(message: string, context?: LoggerContext) { criticalLogger(message, this.#mergeContext(context)) }
}

export const Log = new Logger()

export function createLogger(namespace: string): LoggerObject {
  return {
    debug: logMessage.bind(null, 'debug', namespace),
    info: logMessage.bind(null, 'info', namespace),
    warning: logMessage.bind(null, 'warning', namespace),
    error: logMessage.bind(null, 'error', namespace),
    critical: logMessage.bind(null, 'critical', namespace),
  }
}

function logMessage(level: LoggerLevel, namespace: string, message: string, context: LoggerContext = { }) {
  makeLoggerForLevelAndNamespace(level, namespace)({
    message,
    level,
    namespace,
    timestamp: new Date(),
    context,
  })
}

function makeLoggerForLevelAndNamespace(level: string, namespace: string) {
  const key = `${level}:${namespace}`

  if (mapOfLoggers.has(key)) {
    return mapOfLoggers.get(key)!
  }

  const fn = createDebugger(key)

  mapOfLoggers.set(key, fn)

  return fn
}

